import React, { useContext } from 'react';
import { DeleteOutlined } from '@ant-design/icons';
import { Button } from 'antd';
import { ColumnProps } from 'antd/lib/table';

import IBTooltip from '@totem/components/common/helpTooltips/IBTooltip';
import Tooltip from '@totem/components/common/tooltip/Tooltip';
import OrganizationContext from '@totem/components/orgManagement/orgContext';
import OrgNetSuiteKeysContext from '@totem/components/orgManagement/orgDetails/netSuiteKeys/applied/orgNetSuiteKeysContext';
import { OrganizationNetSuiteCustomer } from '@totem/components/orgManagement/orgDetails/netSuiteKeys/types';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';
import { permissions } from '@totem/components/userContext/security/permissions/permissions';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { isNotNull } from '@totem/utilities/common';
import {
  sortBooleanAscending,
  sortStringAscending,
} from '@totem/utilities/tableUtilities';

const styles = {
  iconButton: {
    marginLeft: '0.4rem',
  },
};

const OrgNetSuiteKeysTable = () => {
  const { userProfile } = useContext(UserProfileContext);
  const { onUpdateNetSuiteKey } = useContext(OrganizationContext);
  const { customers } = useContext(OrgNetSuiteKeysContext);
  const columns: ColumnProps<OrganizationNetSuiteCustomer>[] = [
    {
      title: 'NetSuite Key',
      dataIndex: 'netSuiteKey',
      key: 'netSuiteKey',
      render: (_, record: OrganizationNetSuiteCustomer) => (
        <span>{record.netSuiteKey}</span>
      ),
      defaultSortOrder: 'ascend',
      sorter: (compA, compB) =>
        sortStringAscending(compA.netSuiteKey, compB.netSuiteKey),
    },
    {
      title: 'Company',
      dataIndex: 'company',
      key: 'company',
      render: (_, record: OrganizationNetSuiteCustomer) => (
        <span>{record.company}</span>
      ),
      defaultSortOrder: 'ascend',
      sorter: (compA, compB) =>
        sortStringAscending(compA.company, compB.company),
    },
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      render: (_, record: OrganizationNetSuiteCustomer) => (
        <span>{record.title}</span>
      ),
      defaultSortOrder: 'ascend',
      sorter: (compA, compB) => sortStringAscending(compA.title, compB.title),
    },
    {
      title: 'Is Person',
      dataIndex: 'isPerson',
      key: 'isPerson',
      render: (_, record: OrganizationNetSuiteCustomer) => (
        <span>{record.isPerson ? 'True' : 'False'}</span>
      ),
      defaultSortOrder: 'ascend',
      sorter: (compA, compB) =>
        sortBooleanAscending(compA.isPerson, compB.isPerson),
    },
    {
      title: 'Is Inactive',
      dataIndex: 'isInactive',
      key: 'isInactive',
      render: (_, record: OrganizationNetSuiteCustomer) => (
        <span>{record.isInactive ? 'True' : 'False'}</span>
      ),
      defaultSortOrder: 'ascend',
      sorter: (compA, compB) =>
        sortBooleanAscending(compA.isInactive, compB.isInactive),
    },
    {
      title: 'Actions',
      dataIndex: 'actions',
      key: 'actions',
      render: (_, record: OrganizationNetSuiteCustomer) => (
        <>
          {permissions.internalSystemAdministratorIBOrg.hasPermissions(
            userProfile,
          ) && (
            <IBTooltip
              content={permissions.internalSystemAdministratorIBOrg.tooltip}
            >
              <Tooltip title="Remove key from organization" placement="top">
                <Button
                  type="primary"
                  shape="circle"
                  size="small"
                  icon={<DeleteOutlined />}
                  onClick={() =>
                    onUpdateNetSuiteKey(
                      'DELETE',
                      record.organizationId,
                      record.netSuiteKey,
                    )
                  }
                  style={styles.iconButton}
                />
              </Tooltip>
            </IBTooltip>
          )}
        </>
      ),
    },
  ];

  return (
    <>
      <IBTable
        showSorterTooltip
        columns={columns}
        dataSource={isNotNull(customers) ? customers : []}
        loading={false}
        rowKey={(record) => record.netSuiteKey}
        pagination={{
          showSizeChanger: true,
        }}
      />
    </>
  );
};

export default OrgNetSuiteKeysTable;
